// Invoice Register — sequential legal invoices with Excel export.
import FiscalCell from './FiscalCell';
import React, { useEffect, useState } from 'react';
import { Download, FileText, Loader2, Search, Send } from 'lucide-react';
import { API_URL, apiCall, getToken } from './_shared';

const eur = (v) => `€${Number(v || 0).toFixed(2)}`;
const now = new Date();

const InvoiceRegisterTab = ({ initialQ, userRole }) => {
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState('');
  const [q, setQ] = useState(initialQ || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkPay, setBulkPay] = useState('cash');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year });
      if (month) params.set('month', month);
      if (q.trim()) params.set('q', q.trim());
      setData(await apiCall(`/api/admin/finance/invoices?${params}`));
      setSelected(new Set());
    } catch (e) { alert(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [year, month]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportXlsx = async () => {
    const params = new URLSearchParams({ year });
    if (month) params.set('month', month);
    const r = await fetch(`${API_URL}/api/admin/finance/invoices/export.xlsx?${params}`, {
      credentials: 'include', headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!r.ok) { alert('Export failed'); return; }
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `invoices_${year}${month ? `_${String(month).padStart(2, '0')}` : ''}.xlsx`;
    a.click();
  };

  const selectable = (r) => r.fiscal?.status !== 'fiscalized';
  const selectableRows = (data?.invoices || []).filter(selectable);
  const toggle = (n) => setSelected((prev) => { const next = new Set(prev); next.has(n) ? next.delete(n) : next.add(n); return next; });
  const toggleAll = () => setSelected((prev) => (prev.size === selectableRows.length ? new Set() : new Set(selectableRows.map((r) => r.invoice_number))));

  const bulkFiscalize = async () => {
    const list = [...selected];
    if (!list.length) return;
    if (!window.confirm(`Fiscalize ${list.length} invoice(s) as ${bulkPay.replace('_', ' ')} with the Tax Administration? Invoices that already carry a payment type from a previous attempt keep it.`)) return;
    setBulkBusy(true); setBulkMsg('');
    try {
      const r = await apiCall('/api/admin/fiscal/invoices/bulk-fiscalize', { method: 'POST', body: JSON.stringify({ invoice_numbers: list, payment: bulkPay }) });
      const failed = r.results.filter((x) => x.status === 'failed').map((x) => `${x.invoice_number}: ${x.error}`);
      setBulkMsg(`${r.fiscalized} fiscalized · ${r.failed} failed · ${r.skipped} skipped${failed.length ? ` — ${failed.slice(0, 3).join(' · ')}${failed.length > 3 ? ' …' : ''}` : ''}`);
      await load();
    } catch (e) { setBulkMsg(e.message); } finally { setBulkBusy(false); }
  };

  const downloadPdf = async (n) => {
    const r = await fetch(`${API_URL}/api/admin/fiscal/invoices/${encodeURIComponent(n)}/pdf`, {
      credentials: 'include', headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!r.ok) { alert('PDF failed'); return; }
    const a = document.createElement('a');
    a.href = URL.createObjectURL(await r.blob());
    a.download = `invoice_${n}.pdf`;
    a.click();
  };

  const rows = (data?.invoices || []).map((r) => (
    <tr key={r.invoice_number} className={`border-b last:border-0 ${r.storno_by ? 'bg-red-50/40' : r.kind === 'storno' ? 'bg-violet-50/40' : ''}`} data-testid={`invoice-row-${r.invoice_number}`}>
      <td className="py-2 pl-3 pr-0 w-6">
        {selectable(r) && <input type="checkbox" checked={selected.has(r.invoice_number)} onChange={() => toggle(r.invoice_number)} aria-label={`Select ${r.invoice_number}`} data-testid={`invoice-select-${r.invoice_number}`} />}
      </td>
      <td className="py-2 px-3 font-mono font-semibold text-slate-900">
        <span className={r.storno_by ? 'line-through text-slate-500' : ''}>{r.invoice_number}</span>
        {r.kind === 'storno' && <div className="text-[10px] font-sans font-semibold text-violet-700" data-testid={`invoice-storno-badge-${r.invoice_number}`}>STORNO of {r.storno_of}</div>}
        {r.storno_by && <div className="text-[10px] font-sans font-semibold text-red-600" data-testid={`invoice-cancelled-badge-${r.invoice_number}`}>CANCELLED · {r.storno_by}</div>}
      </td>
      <td className="py-2 px-3 text-slate-600">{(r.issued_at || '').slice(0, 10)}</td>
      <td className="py-2 px-3">{r.guest_name}</td>
      <td className="py-2 px-3 text-slate-600">{r.room_name}</td>
      <td className="py-2 px-3 text-slate-500 text-xs">{r.check_in} → {r.check_out}</td>
      <td className={`py-2 px-3 text-right font-semibold ${r.gross_total < 0 ? 'text-red-600' : ''}`}>{eur(r.gross_total)}</td>
      <td className="py-2 px-3 text-right text-slate-600">{eur(r.payments_total)}</td>
      <td className={`py-2 px-3 text-right ${r.balance > 0.009 ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>{eur(r.balance)}</td>
      <td className="py-2 px-3 text-xs text-slate-500">{r.issued_by}</td>
      <td className="py-2 px-3">
        <button type="button" onClick={() => downloadPdf(r.invoice_number)} className="inline-flex items-center gap-1 text-xs border rounded px-2 py-0.5 hover:bg-slate-50" data-testid={`invoice-pdf-${r.invoice_number}`}><FileText className="w-3 h-3" /> PDF</button>
      </td>
      <td className="py-2 px-3"><FiscalCell invoice={r} onChanged={load} userRole={userRole} /></td>
    </tr>
  ));

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => (
    <option key={m} value={i + 1}>{m}</option>
  ));

  return (
    <div className="space-y-4" data-testid="invoice-register-tab">
      <div className="bg-white rounded-xl border p-3 flex flex-wrap items-center gap-2">
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="border rounded-lg px-2 py-1.5 text-sm" data-testid="invoice-year">
          {[0, 1, 2].map((d) => <option key={d} value={now.getFullYear() - d}>{now.getFullYear() - d}</option>)}
        </select>
        <select value={month} onChange={(e) => setMonth(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm" data-testid="invoice-month">
          <option value="">All months</option>{months}
        </select>
        <div className="flex items-center gap-1 border rounded-lg px-2 flex-1 min-w-[180px]">
          <Search className="w-4 h-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder="Guest, invoice no or booking id" className="py-1.5 text-sm w-full outline-none" data-testid="invoice-search" />
        </div>
        <button onClick={load} className="px-3 py-1.5 rounded-lg border text-sm hover:bg-slate-50">Search</button>
        <button onClick={exportXlsx} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold flex items-center gap-1 hover:bg-emerald-700" data-testid="invoice-export">
          <Download className="w-4 h-4" /> Excel
        </button>
        {data && <span className="text-xs text-slate-500 ml-auto" data-testid="invoice-summary">{data.count} invoices · {eur(data.total_gross)} gross</span>}
      </div>
      {selectableRows.length > 0 && (
        <div className="bg-slate-900 text-white rounded-xl p-3 flex flex-wrap items-center gap-2 text-sm" data-testid="bulk-fiscalize-bar">
          <span className="font-semibold" data-testid="bulk-selected-count">{selected.size} of {selectableRows.length} unfiscalized selected</span>
          <button type="button" onClick={toggleAll} className="text-xs underline text-slate-300" data-testid="bulk-select-all">{selected.size === selectableRows.length ? 'Clear' : 'Select all unfiscalized'}</button>
          <span className="flex-1" />
          <select value={bulkPay} onChange={(e) => setBulkPay(e.target.value)} className="border rounded px-2 py-1 text-slate-900 text-xs" data-testid="bulk-pay">
            <option value="cash">Cash</option><option value="card">Card</option><option value="bank_transfer">Bank transfer</option><option value="ota">OTA prepaid</option><option value="other">Other</option>
          </select>
          <button type="button" onClick={bulkFiscalize} disabled={bulkBusy || !selected.size} className="inline-flex items-center gap-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg px-3 py-1.5 text-sm font-semibold disabled:opacity-50" data-testid="bulk-fiscalize">
            {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Fiscalize selected ({selected.size})
          </button>
          {bulkMsg && <div className="w-full text-xs text-slate-200" data-testid="bulk-msg">{bulkMsg}</div>}
        </div>
      )}
      <div className="bg-white rounded-xl border overflow-x-auto">
        {loading ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500">
              <tr><th className="py-2 pl-3 pr-0 w-6">{selectableRows.length > 0 && <input type="checkbox" checked={selected.size > 0 && selected.size === selectableRows.length} onChange={toggleAll} aria-label="Select all unfiscalized" data-testid="bulk-select-all-header" />}</th><th className="text-left py-2 px-3">Invoice No</th><th className="text-left py-2 px-3">Issued</th><th className="text-left py-2 px-3">Guest</th><th className="text-left py-2 px-3">Room</th><th className="text-left py-2 px-3">Stay</th><th className="text-right py-2 px-3">Gross</th><th className="text-right py-2 px-3">Paid</th><th className="text-right py-2 px-3">Balance</th><th className="text-left py-2 px-3">By</th><th className="text-left py-2 px-3">Doc</th><th className="text-left py-2 px-3">Fiscal</th></tr>
            </thead>
            <tbody>{rows.length ? rows : <tr><td colSpan={12} className="py-8 text-center text-slate-400">No invoices in this period. Numbers are assigned the first time a folio's PDF invoice is generated.</td></tr>}</tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default InvoiceRegisterTab;
